import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { apiService } from '../../services/api';
import UniversalModal from '../Common/UniversalModal';
import UnsavedChangesPopover from '../Common/UnsavedChangesPopover';
import Toast from '../Common/Toast';
import AccountForm from './AccountForm';
import DebtorFormModal from '../Common/DebtorFormModal';
import OrganizerFormModal from '../Common/OrganizerFormModal';
import Pagination from '../Common/Pagination';
import '../Common/CardActions.css';
import '../Common/PrimaryActionButton.css';
import './Accounts.css';
import OwnerBadge from '../Common/OwnerBadge';

const AccountsList = () => {
  const [accounts, setAccounts] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [modalState, setModalState] = useState({
    isOpen: false,
    mode: null, // 'create', 'view', 'edit'
    account: null
  });
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [formData, setFormData] = useState({
    account_number: '',
    bank_name: '',
    bik: '',
    corr_account: '',
    owner_type: 'platform',
    owner_id: '',
    purpose: 'deposit'
  });
  const [toast, setToast] = useState(null);
  const [debtors, setDebtors] = useState([]);
  const [organizers, setOrganizers] = useState([]);
  const [etps, setEtps] = useState([]);
  const [showDebtorForm, setShowDebtorForm] = useState(false);
  const [createdDebtorDraft, setCreatedDebtorDraft] = useState(null);
  const [editDebtorModal, setEditDebtorModal] = useState({ isOpen: false, debtor: null });
  const [showOrganizerForm, setShowOrganizerForm] = useState(false);
  const [organizerFormFixedType, setOrganizerFormFixedType] = useState('specialized_org');
  const [editOrganizerModal, setEditOrganizerModal] = useState({ isOpen: false, organizer: null, fixedType: null });
  const [showOrganizerTypeModal, setShowOrganizerTypeModal] = useState(false);
  
  // Состояние для пагинации
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(24);
  
  // Состояние для диалога подтверждения
  const [confirmDialog, setConfirmDialog] = useState({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: null,
    confirmText: 'Удалить',
    cancelText: 'Отмена'
  });

  const getApiErrorMessage = (error, fallback = 'Неизвестная ошибка') => {
    const data = error?.response?.data;
    if (!data) return error?.message || fallback;
    if (typeof data === 'string') return data;
    if (typeof data === 'object') {
      const accountErrors = data.account_number;
      const accountMessages = Array.isArray(accountErrors) ? accountErrors : (accountErrors ? [accountErrors] : []);
      const hasDuplicateAccountError = accountMessages.some((item) => String(item || '').toLowerCase().includes('уже существует'));
      if (hasDuplicateAccountError) {
        return 'Такой счет уже существует в системе.';
      }
      if (data.detail) return String(data.detail);
      if (data.message) return String(data.message);
      const flattened = Object.values(data).flat().map((item) => String(item || '').trim()).filter(Boolean);
      if (flattened.length) return flattened.join(', ');
    }
    return error?.message || fallback;
  };

  const fetchAccounts = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const all = [];
      let page = 1;
      while (true) {
        const response = await apiService.getBankAccounts(`?page=${page}&page_size=96`);
        const pageResults = response.data?.results || response.data || [];
        all.push(...pageResults);
        if (!response.data?.next) break;
        page += 1;
        if (page > 200) break;
      }
      setAccounts(all);
    } catch (err) {
      console.error('Error fetching accounts:', err);
      setError('Ошибка загрузки банковских счетов: ' + (err.response?.data?.detail || err.message));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAccounts();
  }, [fetchAccounts]);

  const fetchAllDebtors = useCallback(async () => {
    const all = [];
    let page = 1;
    while (true) {
      const resp = await apiService.getDebtors(`?page=${page}&page_size=96`);
      const chunk = resp.data?.results || [];
      all.push(...chunk);
      if (!resp.data?.next) break;
      page += 1;
      if (page > 500) break;
    }
    return all;
  }, []);

  const fetchAllOrganizers = useCallback(async () => {
    const all = [];
    let page = 1;
    while (true) {
      const resp = await apiService.getOrganizers(`?page=${page}&page_size=96`);
      const chunk = resp.data?.results || [];
      all.push(...chunk);
      if (!resp.data?.next) break;
      page += 1;
      if (page > 500) break;
    }
    return all;
  }, []);

  const fetchAllEtps = useCallback(async () => {
    const all = [];
    let page = 1;
    while (true) {
      const resp = await apiService.getEtps(`?page=${page}`);
      const chunk = resp.data?.results || [];
      all.push(...chunk);
      if (!resp.data?.next) break;
      page += 1;
      if (page > 500) break;
    }
    return all;
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const [debtorsData, organizersData, etpsData] = await Promise.all([
          fetchAllDebtors(),
          fetchAllOrganizers(),
          fetchAllEtps(),
        ]);
        setDebtors(debtorsData || []);
        setOrganizers(organizersData || []);
        setEtps(etpsData || []);
      } catch (_) {
        setDebtors([]);
        setOrganizers([]);
        setEtps([]);
      }
    })();
  }, [fetchAllDebtors, fetchAllOrganizers, fetchAllEtps]);

  useEffect(() => {
    (async () => {
      try {
        const resp = await apiService.getUiSettings();
        const v = Number(resp?.data?.table_page_size);
        if ([12, 24, 48, 96].includes(v)) {
          setItemsPerPage(v);
          setCurrentPage(1);
        }
      } catch (_) {
        // ignore
      }
    })();
  }, []);

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 2500);
  };

  const resetForm = () => {
    setFormData({
      account_number: '',
      bank_name: '',
      bik: '',
      corr_account: '',
      owner_type: 'platform',
      owner_id: '',
      purpose: 'deposit'
    });
    setHasUnsavedChanges(false);
  };
  
  const handleCancelModal = () => {
    setModalState({ isOpen: false, mode: null, account: null });
    setHasUnsavedChanges(false);
    resetForm();
  };

  const handleAddAccount = () => {
    resetForm();
    setModalState({ isOpen: true, mode: 'create', account: null });
  };

  const handleViewAccount = (account) => {
    setHasUnsavedChanges(false);
    setModalState({ isOpen: true, mode: 'view', account });
  };

  const handleEditAccount = (account) => {
    setHasUnsavedChanges(false);
    setModalState({ isOpen: true, mode: 'edit', account });
  };

  const handleStartEdit = () => {
    setHasUnsavedChanges(false);
    setModalState({ ...modalState, mode: 'edit' });
  };

  const handleDeleteAccount = (account) => {
    showConfirmDialog(
      'Подтверждение удаления',
      `Вы уверены, что хотите удалить банковский счет ${account.account_number}? Это действие нельзя отменить.`,
      async () => {
        try {
          await apiService.deleteBankAccount(account.id);
          setAccounts(accounts.filter(a => a.id !== account.id));
        } catch (err) {
          setError('Ошибка при удалении банковского счета: ' + (err.response?.data?.detail || err.message));
        }
      }
    );
  };

  const handleSaveAccount = async (payload, { exit } = {}) => {
    try {
      setError('');

      const dataToSend = {
        ...payload,
        owner_id: payload.owner_id ? parseInt(payload.owner_id, 10) : null
      };

      let savedAccount = null;

      if (modalState.account) {
        const response = await apiService.updateBankAccount(modalState.account.id, dataToSend);
        savedAccount = response.data || { ...modalState.account, ...payload };
        setAccounts(accounts.map(a => a.id === savedAccount.id ? savedAccount : a));
      } else {
        const response = await apiService.createBankAccount(dataToSend);
        savedAccount = response.data;
      setAccounts([savedAccount, ...accounts]);
      }

      setHasUnsavedChanges(false);

      showToast(modalState.account ? 'Изменения сохранены' : 'Банковский счет создан');

      if (exit) {
        setModalState({ isOpen: false, mode: null, account: null });
        resetForm();
      } else {
        setModalState({ isOpen: true, mode: 'edit', account: savedAccount });
      }

      return savedAccount;
    } catch (err) {
      console.error('Error saving account:', err.response?.data || err);
      const errorMessage = `Ошибка при сохранении банковского счета: ${getApiErrorMessage(err, 'Неизвестная ошибка')}`;
      showToast(errorMessage, 'error');
      return false;
    }
  };

  const handleAccountOwnerCreate = (ownerType) => {
    if (ownerType === 'debtor') {
      setCreatedDebtorDraft(null);
      setShowDebtorForm(true);
      return;
    }
    if (ownerType === 'organizer') {
      setShowOrganizerTypeModal(true);
    }
  };

  const handleAccountOwnerEdit = (ownerType, ownerId) => {
    if (ownerType === 'debtor') {
      const debtor = debtors.find(d => d.id === ownerId);
      if (debtor) {
        setEditDebtorModal({ isOpen: true, debtor });
      }
    } else if (ownerType === 'organizer') {
      const organizer = organizers.find(o => o.id === ownerId);
      if (organizer) {
        setEditOrganizerModal({ isOpen: true, organizer, fixedType: organizer?.type || null });
      }
    }
  };

  const getPurposeText = (purpose) => {
    const key = String(purpose || '').trim().toLowerCase();
    const purposes = {
      deposit: 'Задаток',
      payment: 'Оплата по договору',
      other: 'Прочее'
    };
    return purposes[key] || purposes.other;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('ru-RU');
  };

  // Функции для управления диалогом подтверждения
  const showConfirmDialog = (title, message, onConfirm, confirmText = 'Удалить') => {
    setConfirmDialog({
      isOpen: true,
      title,
      message,
      onConfirm,
      confirmText,
      cancelText: 'Отмена'
    });
  };

  const hideConfirmDialog = () => {
    setConfirmDialog(prev => ({ ...prev, isOpen: false }));
  };

  const handleConfirmAction = () => {
    if (confirmDialog.onConfirm) {
      confirmDialog.onConfirm();
    }
    hideConfirmDialog();
  };

  // Обработчики пагинации
  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  const handleItemsPerPageChange = (newItemsPerPage) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1); // Сбрасываем на первую страницу при изменении количества элементов
    apiService.saveUiSettings({ table_page_size: newItemsPerPage }).catch(() => {});
  };

  const filtered = useMemo(() => {
    const q = String(search || '').trim().toLowerCase();
    if (!q) return accounts;
    return (accounts || []).filter((x) => {
      const hay = `${x.account_number || ''} ${x.bank_name || ''} ${x.bik || ''} ${x.corr_account || ''}`.toLowerCase();
      return hay.includes(q);
    });
  }, [accounts, search]);

  useEffect(() => {
    setCurrentPage(1);
  }, [search, itemsPerPage, accounts]);

  const totalItems = filtered.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage) || 1;
  const paginated = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    return filtered.slice(start, start + itemsPerPage);
  }, [filtered, currentPage, itemsPerPage]);

  if (loading) {
    return (
      <div style={{ padding: '50px', textAlign: 'center' }}>
        <h2>Загрузка банковских счетов...</h2>
      </div>
    );
  }

  return (
    <div className="accounts-container">
      <div className="accounts-header">
        <h1>💳 Банковские счета</h1>
        <button 
          className="primary-create-button"
          onClick={handleAddAccount}
        >
          <span className="primary-create-button__icon">+</span>
          Добавить счет
        </button>
      </div>

      {error && (
        <div className="error-message">
          <span>{error}</span>
          <button onClick={fetchAccounts} className="btn btn-sm">
            Повторить
          </button>
        </div>
      )}

      <div style={{ display: 'flex', gap: 10, marginBottom: 16, alignItems: 'center' }}>
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Поиск по счету, банку, БИК, корр. счету..."
          style={{ flex: 1, minWidth: 260, padding: 10, border: '1px solid #dee2e6', borderRadius: 10 }}
        />
        <div style={{ color: '#6c757d', whiteSpace: 'nowrap' }}>
          Найдено: <b>{filtered.length}</b>
        </div>
      </div>

      <div>
        {filtered.length === 0 ? (
          <div className="empty-state">
            <span className="empty-icon">💳</span>
            <p>У вас пока нет банковских счетов</p>
            <small>Создайте новый банковский счет для торгов</small>
          </div>
        ) : (
          <div className="accounts-table-container">
            <table className="accounts-table">
              <thead>
                <tr>
                  <th>Номер счета</th>
                  <th>Банк</th>
                  <th>БИК</th>
                  <th>Корр. счет</th>
                  <th>Назначение</th>
                  <th>Дата создания</th>
                  <th>Действия</th>
                </tr>
              </thead>
              <tbody>
                {paginated.map((account) => (
                  <tr 
                    key={account.id}
                    className="account-row clickable"
                    onClick={() => handleViewAccount(account)}
                    style={{ cursor: 'pointer' }}
                  >
                    <td className="account-number">
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                        <span>{account.account_number}</span>
                        <OwnerBadge email={account.owner_email} />
                      </div>
                    </td>
                    <td className="bank-name">{account.bank_name}</td>
                    <td className="bik">{account.bik}</td>
                    <td className="corr-account">{account.corr_account}</td>
                <td className="purpose">
                      <span className={`purpose-badge ${String(account.purpose || '').trim().toLowerCase()}`}>
                        {getPurposeText(account.purpose)}
                      </span>
                    </td>
                    <td className="created-date">{formatDate(account.created_date)}</td>
                    <td className="actions">
                      <div className="card-actions">
                        <button
                          type="button"
                          className="card-action-btn card-action-btn--edit"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleEditAccount(account);
                          }}
                          title="Редактировать"
                        >
                          ✎
                        </button>
                        <button
                          type="button"
                          className="card-action-btn card-action-btn--delete"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteAccount(account);
                          }}
                          title="Удалить"
                        >
                          🗑️
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        
        {/* Пагинация */}
        {totalItems > 0 && (
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={handlePageChange}
            itemsPerPage={itemsPerPage}
            totalItems={totalItems}
            onItemsPerPageChange={handleItemsPerPageChange}
          />
        )}
      </div>

      {/* Модальное окно для работы с банковскими счетами */}
      <UniversalModal
        isOpen={modalState.isOpen}
        onClose={handleCancelModal}
        title={
          modalState.account 
            ? (modalState.mode === 'view' ? `Банковский счет: ${modalState.account.account_number}` : 'Редактирование банковского счета')
            : 'Создание банковского счета'
        }
        isViewMode={modalState.mode === 'view'}
        onEdit={handleStartEdit}
        hasChanges={hasUnsavedChanges}
        modalClassName="universal-modal--wide"
      >
            <AccountForm
              account={modalState.account}
              formData={formData}
              setFormData={setFormData}
              onSave={handleSaveAccount}
              onCancel={handleCancelModal}
              isViewMode={modalState.mode === 'view'}
              onFormChange={setHasUnsavedChanges}
              debtors={debtors}
              organizers={organizers}
              etps={etps}
              onCreateOwner={handleAccountOwnerCreate}
              onEditOwner={handleAccountOwnerEdit}
            />
      </UniversalModal>

      {/* Диалог подтверждения */}
      {confirmDialog.isOpen && (
        <ConfirmDialog 
          confirmDialog={confirmDialog}
          onCancel={hideConfirmDialog}
          onConfirm={handleConfirmAction}
        />
      )}

      {showDebtorForm && (
        <DebtorFormModal
          debtor={createdDebtorDraft}
          showToast={showToast}
          onSave={async (debtorData, { exit } = {}) => {
            try {
              let savedDebtor = null;
              const isUpdate = Boolean(createdDebtorDraft?.id);
              if (createdDebtorDraft?.id) {
                const response = await apiService.updateDebtor(createdDebtorDraft.id, debtorData);
                savedDebtor = response.data || { ...createdDebtorDraft, ...debtorData };
              } else {
                const response = await apiService.createDebtor(debtorData);
                savedDebtor = response.data;
              }
              if (savedDebtor?.id) {
                setCreatedDebtorDraft(savedDebtor);
                setDebtors(prev => {
                  const exists = prev.some(d => d.id === savedDebtor.id);
                  if (exists) {
                    return prev.map(d => (d.id === savedDebtor.id ? savedDebtor : d));
                  }
                  return [savedDebtor, ...prev];
                });
              }
              if (exit) {
                setShowDebtorForm(false);
                setCreatedDebtorDraft(null);
              }
              showToast(isUpdate ? 'Должник успешно обновлен' : 'Должник успешно создан', 'success');
              return savedDebtor;
            } catch (error) {
              const message = getApiErrorMessage(error, 'Не удалось сохранить должника');
              showToast(`Ошибка при сохранении должника: ${message}`, 'error');
              return false;
            }
          }}
          onCancel={() => {
            setShowDebtorForm(false);
            setCreatedDebtorDraft(null);
          }}
        />
      )}

      {editDebtorModal.isOpen && (
        <DebtorFormModal
          debtor={editDebtorModal.debtor}
          showToast={showToast}
          onSave={async (debtorData, { exit } = {}) => {
            try {
              const response = await apiService.updateDebtor(editDebtorModal.debtor.id, debtorData);
              const updatedDebtor = response.data;
              setDebtors(debtors.map(d => d.id === editDebtorModal.debtor.id ? updatedDebtor : d));
              setEditDebtorModal(prev =>
                exit
                  ? { isOpen: false, debtor: null }
                  : { ...prev, isOpen: true, debtor: updatedDebtor }
              );
              showToast('Должник успешно обновлен', 'success');
              return updatedDebtor;
            } catch (error) {
              showToast(`Ошибка при обновлении должника: ${error.message}`, 'error');
              return false;
            }
          }}
          onCancel={() => setEditDebtorModal({ isOpen: false, debtor: null })}
        />
      )}

      {showOrganizerForm && (
        <OrganizerFormModal
          fixedType={organizerFormFixedType}
          onSave={async (organizerData, { exit } = {}) => {
            try {
              const response = await apiService.createOrganizer(organizerData);
              const createdOrganizer = response.data;
              setOrganizers(prev => {
                if (!createdOrganizer?.id) {
                  return prev;
                }
                const exists = prev.some(o => o.id === createdOrganizer.id);
                if (exists) {
                  return prev.map(o => (o.id === createdOrganizer.id ? createdOrganizer : o));
                }
                return [createdOrganizer, ...prev];
              });
              if (exit) {
                setShowOrganizerForm(false);
              }
              showToast('Организатор успешно создан', 'success');
              return createdOrganizer;
            } catch (error) {
              showToast(`Ошибка при создании организатора: ${error.message}`, 'error');
              return false;
            }
          }}
          onCancel={() => setShowOrganizerForm(false)}
        />
      )}

      {editOrganizerModal.isOpen && (
        <OrganizerFormModal
          organizer={editOrganizerModal.organizer}
          fixedType={editOrganizerModal.fixedType}
          onSave={async (organizerData, { exit } = {}) => {
            try {
              const response = await apiService.updateOrganizer(editOrganizerModal.organizer.id, organizerData);
              const updatedOrganizer = response.data;
              setOrganizers(organizers.map(o => o.id === editOrganizerModal.organizer.id ? updatedOrganizer : o));
              setEditOrganizerModal(prev =>
                exit
                  ? { isOpen: false, organizer: null, fixedType: null }
                  : { ...prev, isOpen: true, organizer: updatedOrganizer, fixedType: prev.fixedType }
              );
              showToast('Организатор успешно обновлен', 'success');
              return updatedOrganizer;
            } catch (error) {
              showToast(`Ошибка при обновлении организатора: ${error.message}`, 'error');
              return false;
            }
          }}
          onCancel={() => setEditOrganizerModal({ isOpen: false, organizer: null, fixedType: null })}
        />
      )}

      {showOrganizerTypeModal && (
        <div className="modal-overlay" style={{ zIndex: 2500 }}>
          <div className="modal confirm-dialog" style={{ maxWidth: 520, width: 'min(520px, 92vw)' }}>
            <div className="modal-header">
              <h3>Добавить организатора</h3>
            </div>
            <div className="modal-body">
              <p className="confirm-message">Выберите тип добавляемой записи.</p>
              <div className="confirm-actions" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                <button
                  type="button"
                  className="btn btn-primary"
                  style={{ width: '100%' }}
                  onClick={() => {
                    setShowOrganizerTypeModal(false);
                    setOrganizerFormFixedType('specialized_org');
                    setShowOrganizerForm(true);
                  }}
                >
                  ОТ (организатор торгов)
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  style={{ width: '100%' }}
                  onClick={() => {
                    setShowOrganizerTypeModal(false);
                    setOrganizerFormFixedType('arbitrage_manager');
                    setShowOrganizerForm(true);
                  }}
                >
                  АУ (арбитражный управляющий)
                </button>
                <button
                  type="button"
                  className="btn btn-secondary"
                  style={{ width: '100%', gridColumn: '1 / -1' }}
                  onClick={() => setShowOrganizerTypeModal(false)}
                >
                  Отмена
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <Toast toast={toast} />
    </div>
  );
};

// Компонент диалога подтверждения с обработчиком Escape
const ConfirmDialog = ({ confirmDialog, onCancel, onConfirm }) => {
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation(); 
        e.stopImmediatePropagation();
        onCancel();
        return false;
      }
    };

    document.addEventListener('keydown', handleEscape, { capture: true });
    return () => {
      document.removeEventListener('keydown', handleEscape, { capture: true });
    };
  }, [onCancel]);

  return (
    <UnsavedChangesPopover
      isOpen={!!confirmDialog?.isOpen}
      title={confirmDialog?.title || 'Подтверждение'}
      message={confirmDialog?.message}
      onCancel={onCancel}
      onConfirm={onConfirm}
      cancelText={confirmDialog?.cancelText || 'Отмена'}
      confirmText={confirmDialog?.confirmText || 'Подтвердить'}
      zIndex={10000}
    />
  );
};

export default AccountsList;
